import React, { useCallback, useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@emotion/react";
import { useDispatch, useSelector } from "react-redux";
import { checkRoles, getPermissionFlags, getUserScope } from "utils/permissions";
import DataGridCustomToolbar from "components/DataGridCustomToolBar";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  DeleteOutlined,
  EditOutlined,
  CopyAllOutlined,
  // PriorityHighIcon,
} from "@mui/icons-material";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import {
  copyPrincipleQuestion,
  deletePrincipleQuestion,
  fetchPrincipleQuestions,
} from "reduxStore/actions/principleQuestionsActions";
import { setSnackbar } from "reduxStore/state";
import QuestionsForm from "./questionsForm";
import CopyDialog from "./copyDialog";
import DeleteConfirmationDialog from "./deleteConfirmationDialog";
import { fetchAllLocations } from "reduxStore/actions/locationActions";
import { fetchPrinciples } from "reduxStore/actions/principleActions";

function PrincipleQuestionsGrid({
  page,
  setPage,
  limit,
  setLimit,
  search,
  setSearch,
  sort,
  setSort,
  searchInput,
  setSearchInput,
  location,
  setLocation,
}) {
  const { palette } = useTheme();
  const user = useSelector((state) => state.user);

  const { locationIds } = getUserScope(user);
  const userLocationId = locationIds[0] || "";

  const availableLocations = useSelector((state) => state.locationsAll);
  const principles = useSelector((state) => state.principles);

  const { showOrg, showLocation } = getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);

  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const dispatch = useDispatch();

  const questions = useSelector((state) => state.principleQuestions.data || []);
  const totalQuestions = useSelector(
    (state) => state.principleQuestions.total || 0
  );

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // Track if we are in edit mode

  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);

  const [currentPrincipleQuestion, setCurrentPrincipleQuestion] =
    useState(null); // Store the user being edited

  const [principle, setPrinciple] = useState(""); // Store the principle being edited

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  const handleEditOpen = useCallback((principleQuestion) => {
    setEditMode(true);
    setCurrentPrincipleQuestion(principleQuestion);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const openDeleteDialog = (questionId) => {
    setQuestionToDelete(questionId);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setQuestionToDelete(null);
  };

  const handleConfirmCopy = async (selectedSchools) => {
    await handleCopyQuestion(selectedQuestionId, selectedSchools);
    closeCopyDialog();
  };

  const handleDispatchOfPrincipleQuestions = () => {
    dispatch(
      fetchPrincipleQuestions({
        page, // Ensure it aligns with API expectations
        limit, // API expects 'limit' instead of 'pageSize'
        sort,
        search,
        location:
          !showOrg && !isAcademyImprovementLead ? userLocationId : location,
        principle: principle,
      })
    );
  };

  const handleCopyQuestion = async (id, newLocations) => {
    try {
      const response = await dispatch(
        copyPrincipleQuestion(id, newLocations, user.organisation)
      );

      // Ensure response is always an object
      if (!response || typeof response !== "object") {
        throw new Error("Invalid response from server.");
      }

      let message = `✅ ${response.message || "Copy operation completed."}`;
      if (response.duplicateLocations?.length > 0) {
        message += ` ⚠️ Skipped (Already Exists): ${response.duplicateLocations.length} locations.`;
      }
      if (response.failedLocations?.length > 0) {
        message += ` ❌ Failed: ${response.failedLocations.length} locations.`;
      }

      dispatch(
        setSnackbar({
          open: true,
          message,
          severity:
            response.failedLocations?.length > 0 ||
            response.duplicateLocations?.length > 0
              ? "warning"
              : "success",
        })
      );

      // Refresh the list of questions
      handleDispatchOfPrincipleQuestions();

      return response; // Ensure CopyDialog receives a valid response
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          message: "❌ Unexpected error occurred.",
          severity: "error",
        })
      );
      console.error("Error while copying principle question:", error);

      return {
        error: true,
        message: "An unexpected error occurred.",
        failedLocations: [],
        duplicateLocations: [],
      };
    }
  };

  const openCopyDialog = (id) => {
    setSelectedQuestionId(id);
    setCopyDialogOpen(true);
  };

  const closeCopyDialog = () => {
    setCopyDialogOpen(false);
    setSelectedQuestionId(null);
  };

  const handleDeleteConfirm = async () => {
    if (!questionToDelete) return;

    const response = await dispatch(
      deletePrincipleQuestion(questionToDelete, userLocationId)
    );

    dispatch(
      setSnackbar({
        open: true,
        message: response.message,
        severity: response.success ? "success" : "error",
      })
    );

    handleDispatchOfPrincipleQuestions();

    closeDeleteDialog();
  };

  const columns = [
    {
      field: "principleName",
      headerName: "Principle",
      flex: 2,
      valueGetter: (params) =>
        `P${params.row.principle.principleNumber}: ${params.row.principle.name}` ||
        "N/A",
    },

    {
      field: "priority",
      headerName: "Priority",
      flex: 0.5,
      renderCell: (params) =>
        params.row.priority ? (
          <PriorityHighIcon sx={{ color: "orange" }} />
        ) : (
          // empty cell if not priority
          <span style={{ visibility: "hidden" }}>Not Priority</span>
        ),
    },

    {
      field: "question",
      headerName: "Question",
      flex: 2,
    },
    {
      field: "url",
      headerName: "URL",
      flex: 2,
    },
    (showOrg || isAcademyImprovementLead) && {
      field: "location",
      headerName: "Location",
      flex: 1,
      valueGetter: (params) => params.row.location.name || "N/A",
    },
    showLocation && {
      field: "edit",
      headerName: "Edit",
      flex: 0.5,
      renderCell: (params) => (
        <button
          onClick={() => handleEditOpen(params.row)}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <EditOutlined sx={{ color: primaryDark }} />
        </button>
      ),
    },

    showOrg && {
      field: "copy",
      headerName: "Copy",
      flex: 0.5,
      renderCell: (params) => (
        <button
          onClick={() => openCopyDialog(params.row._id)}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <CopyAllOutlined sx={{ color: "blue" }} />
        </button>
      ),
    },

    showLocation && {
      field: "delete",
      headerName: "Delete",
      flex: 0.5,
      renderCell: (params) => (
        <button
          onClick={() => openDeleteDialog(params.row._id)}
          style={{ backgroundColor: "transparent", border: "none" }}
        >
          <DeleteOutlined sx={{ color: "red" }} />
        </button>
      ),
    },
  ].filter(Boolean);

  useEffect(() => {
    if (!showOrg && !isAcademyImprovementLead) {
      setLocation(userLocationId);
    } else {
      setLocation(null);
    }
  }, [showOrg, userLocationId, isAcademyImprovementLead, setLocation]);

  useEffect(() => {
    dispatch(
      fetchPrincipleQuestions({
        page, // Ensure it aligns with API expectations
        limit, // API expects 'limit' instead of 'pageSize'
        sort,
        search,
        location:
          !showOrg && !isAcademyImprovementLead ? userLocationId : location,
        principle: principle,
      })
    );
  }, [
    dispatch,
    userLocationId,
    showOrg,
    page,
    limit,
    sort,
    search,
    principle,
    location,
    isAcademyImprovementLead,
  ]);

  useEffect(() => {
    dispatch(fetchAllLocations());
    dispatch(fetchPrinciples());
  }, [dispatch]);

  // 1) When the user stops typing for 500ms, actually apply the search:
  useEffect(() => {
    const h = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(h);
  }, [searchInput, setSearch, setPage]);

  return (
    <Box
      mt="40px"
      sx={{
        "& .MuiDataGrid-root": {},
        "& .MuiDataGrid-cell": {
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflow: "visible",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: backgroundAlt,
        },
        "& .MuiDataGrid-virtualScroller": {
          backgroundColor: backgroundAlt,
        },
        "& .MuiDataGrid-footerContainer": {
          backgroundColor: primaryLight,
        },
        "& .MuiDataGrid-row": {
          height: "auto", // This ensures the row height adapts to content
        },
      }}
    >
      {questions.length === 0 && (
        <Box mt={2}>
          <Typography variant="h6" color="textSecondary">
            No questions found
          </Typography>
        </Box>
      )}

      {(showOrg || isAcademyImprovementLead) && (
        <Box display="flex" gap={2} alignItems="center" mb={2}>
          <TextField
            select
            label="Principle"
            value={principle || ""}
            onChange={(e) => setPrinciple(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            <MenuItem value="">All Principles</MenuItem>
            {principles.map((principle) => (
              <MenuItem key={principle._id} value={principle._id}>
                {`P${principle.principleNumber}: ${principle.name}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="School"
            value={location || ""}
            onChange={(e) => setLocation(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            <MenuItem value="">All Locations</MenuItem>
            {availableLocations.map((loc) => (
              <MenuItem key={loc._id} value={loc._id}>
                {loc.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}

      <DataGrid
        getRowId={(row) => row._id}
        rows={questions}
        columns={columns}
        page={page - 1} // If API expects zero-based indexing
        pageSize={limit}
        rowCount={totalQuestions}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        onPageChange={(newPage) => {
          setPage(newPage + 1); // Convert zero-based to one-based if necessary
        }}
        onPageSizeChange={(newPageSize) => {
          setPage(1); // Reset to first page when page size changes
          setLimit(newPageSize);
        }}
        paginationMode="server"
        components={{
          Toolbar: DataGridCustomToolbar,
        }}
        componentsProps={{
          toolbar: {
            search,
            setSearch,
            searchInput,
            setSearchInput,
            sort,
            setSort,
          },
        }}
        disableSelectionOnClick
        rowHeight={50}
        autoHeight
      />

      {/* Dialog for Adding/Editing Questions */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode
            ? `Edit Question - ${currentPrincipleQuestion.question}`
            : "Add a New Question"}
        </DialogTitle>
        <DialogContent>
          <QuestionsForm
            editMode={editMode}
            currentPrincipleQuestion={currentPrincipleQuestion}
            setOpen={setOpen}
            handleClose={handleClose}
            page={page}
            limit={limit}
            search={search}
            sort={sort}
            searchInput={searchInput}
            location={location}
          />
        </DialogContent>
      </Dialog>
      {/* Copy Dialog */}
      <CopyDialog
        open={copyDialogOpen}
        handleClose={closeCopyDialog}
        handleConfirmCopy={handleConfirmCopy}
        availableLocations={availableLocations}
      />
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        handleClose={closeDeleteDialog}
        handleConfirm={handleDeleteConfirm}
      />
    </Box>
  );
}

export default PrincipleQuestionsGrid;
